// src/pages/CategoryDetailPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  Skeleton,
  Alert,
  Avatar,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  NavigateNext as NavigateNextIcon,
  Folder as FolderIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useCategoryName, useCategoryDescription } from '../utils/categoryTranslation';
import categoryService from '../services/categoryService';
import CategoryCard from '../components/categories/CategoryCard';

const UPLOADS_BASE_URL =
  process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${UPLOADS_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
};

const CategoryDetailPage = () => {
  const { idOrSlug } = useParams();
  const theme = useTheme();
  const { t } = useTranslation('common');
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();

  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Appel direct au service pour contourner le cache plat du contexte
  // (le cache contexte ne contient ni children ni description)
  useEffect(() => {
    if (!idOrSlug || idOrSlug === 'undefined') {
      setError(t('categoryDetail.invalidId'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    categoryService.getCategory(idOrSlug)
      .then((cat) => {
        if (!cancelled) {
          setCategory(cat);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('categoryDetail.notFound', { idOrSlug }));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [idOrSlug]);

  const imageUrl = getImageUrl(category?.imageUrl);
  const categoryName = category ? getCategoryName(category) : '';
  const categoryDesc = category ? getCategoryDescription(category) : '';
  const parentCategoryName = category?.parent ? getCategoryName(category.parent) : '';
  const children = category?.children ?? [];

  // ── Skeleton ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" width="100%" height={320} />
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Skeleton variant="text" width={300} height={30} sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // ── Erreur ──────────────────────────────────────────────────────────────────
  if (error || !category) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error || t('categoryDetail.notFound', { idOrSlug })}
        </Alert>
        <Button
          component={RouterLink}
          to="/categories"
          startIcon={<ArrowBackIcon />}
          variant="contained"
          sx={{
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            borderRadius: 2,
            '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
          }}
        >
          {t('categoryDetail.backToCategories')}
        </Button>
      </Container>
    );
  }

  // ── Page ────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── HERO BANNER ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: 260, md: 360 },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        {/* Image ou gradient de fond */}
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={categoryName}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, #6200ee 0%, #03dac4 100%)',
            }}
          />
        )}

        {/* Overlay sombre pour la lisibilité */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: imageUrl
              ? 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 60%, transparent 100%)'
              : 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 100%)',
          }}
        />

        {/* Contenu Hero */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 4, pt: { xs: 3, md: 5 } }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />}
            aria-label="breadcrumb"
            sx={{ mb: 2 }}
          >
            <Link component={RouterLink} to="/" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
              {t('navigation.home')}
            </Link>
            <Link component={RouterLink} to="/categories" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
              {t('navigation.categories')}
            </Link>
            {category.parent && (
              <Link
                component={RouterLink}
                to={`/categories/${category.parent.slug}`}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
              >
                {parentCategoryName}
              </Link>
            )}
            <Typography sx={{ color: 'white', fontWeight: 600 }}>{categoryName}</Typography>
          </Breadcrumbs>

          {/* Titre */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {!imageUrl && (
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <CategoryIcon sx={{ fontSize: 28, color: 'white' }} />
                </Avatar>
              )}
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: 'white',
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  fontSize: { xs: '2rem', md: '2.75rem' }
                }}
              >
                {categoryName}
              </Typography>
              {category.offer_type && (
                <Chip
                  label={t(`listing.form.offerTypes.${category.offer_type}`)}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.35)',
                    fontWeight: 600,
                    backdropFilter: 'blur(8px)'
                  }}
                />
              )}
            </Box>

            {categoryDesc && (
              <Typography
                variant="h6"
                sx={{
                  mt: 1.5,
                  color: 'rgba(255,255,255,0.9)',
                  maxWidth: 720,
                  fontWeight: 400,
                  textShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  fontSize: { xs: '1rem', md: '1.15rem' }
                }}
              >
                {categoryDesc}
              </Typography>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* ── CORPS ───────────────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        {/* Bouton retour + lien parent */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Button
            component={RouterLink}
            to="/categories"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.4),
              color: theme.palette.primary.main,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.06),
                borderColor: theme.palette.primary.main
              }
            }}
          >
            {t('categoryDetail.backToCategories')}
          </Button>

          {category.parent && (
            <Chip
              icon={<FolderIcon />}
              label={t('categoryDetail.parentLabel', { name: parentCategoryName })}
              component={RouterLink}
              to={`/categories/${category.parent.slug}`}
              clickable
              sx={{
                background: `linear-gradient(45deg, ${alpha('#6200ee', 0.1)} 30%, ${alpha('#03dac4', 0.1)} 90%)`,
                border: `1px solid ${alpha('#6200ee', 0.25)}`,
                color: '#6200ee',
                fontWeight: 600,
                '&:hover': { background: alpha('#6200ee', 0.15) }
              }}
            />
          )}

          {children.length > 0 && (
            <Chip
              label={t('categoryDetail.subCategoriesCountBadge', { count: children.length })}
              sx={{
                background: `linear-gradient(45deg, ${alpha('#03dac4', 0.12)} 0%, ${alpha('#6200ee', 0.12)} 100%)`,
                border: `1px solid ${alpha('#03dac4', 0.3)}`,
                color: '#6200ee',
                fontWeight: 600
              }}
            />
          )}
        </Box>

        {/* ── SOUS-CATÉGORIES ─────────────────────────────────────────────────── */}
        {children.length > 0 ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Titre de section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  width: 4,
                  height: 28,
                  borderRadius: 2,
                  background: 'linear-gradient(180deg, #6200ee 0%, #03dac4 100%)'
                }}
              />
              <Typography variant="h5" fontWeight={700}>
                {t('categoryDetail.sectionTitle')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <AnimatePresence>
                {children.map((child, index) => (
                  <Grid item xs={12} sm={6} md={4} key={child.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.07 }}
                    >
                      <CategoryCard
                        category={{
                          ...child,
                          imageUrl: getImageUrl(child.imageUrl)
                        }}
                      />
                    </motion.div>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          </motion.div>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha('#6200ee', 0.04)} 0%, ${alpha('#03dac4', 0.06)} 100%)`,
              border: `1px dashed ${alpha('#6200ee', 0.2)}`,
              gap: 2
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha('#6200ee', 0.08),
                border: `2px solid ${alpha('#6200ee', 0.2)}`
              }}
            >
              <InfoIcon sx={{ fontSize: 32, color: '#6200ee' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" fontWeight={600} textAlign="center">
              {t('categoryDetail.noSubCategoriesTitle')}
            </Typography>
            <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={400}>
              {t('categoryDetail.noSubCategoriesMessage')}
            </Typography>
            <Button
              component={RouterLink}
              to="/categories"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{
                mt: 1,
                borderColor: alpha('#6200ee', 0.4),
                color: '#6200ee',
                borderRadius: 2,
                '&:hover': { background: alpha('#6200ee', 0.06) }
              }}
            >
              {t('categoryDetail.exploreAll')}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CategoryDetailPage;
